import { useState, useEffect } from 'react'
import { COMPETENCIES, KNOWLEDGE, type CompetencyCode, type KnowledgeCode, type Question, type Exam, type QuestionType } from '../types'
import { normalizeQuestion, questions as defaultQuestions } from '../data/questions'
import type { UserProfile } from '../components/AuthModal'

interface AdminProps {
  user?: UserProfile | null
  go: (route: string) => void
  openAuth?: () => void
}

export function Admin({ user, go, openAuth }: AdminProps) {
  const [questions, setQuestions] = useState<Question[]>(() => {
    const saved = localStorage.getItem('sangmath_custom_questions')
    try { return saved ? JSON.parse(saved).map(normalizeQuestion) : defaultQuestions } catch { return defaultQuestions }
  })

  const [activeTab, setActiveTab] = useState<'list' | 'create' | 'export'>('list')
  const [editingId, setEditingId] = useState<string | null>(null)

  // Strict Admin Verification
  const isAdmin = user?.role === 'admin'

  // Form State
  const [exam, setExam] = useState<Exam>('HSA')
  const [domain, setDomain] = useState('Tư duy Định lượng')
  const [topic, setTopic] = useState('Đại số & Suy luận')
  const [knowledge, setKnowledge] = useState<KnowledgeCode>('algebra')
  const [competencies, setCompetencies] = useState<CompetencyCode>('problem-solving')
  const [cognitiveLevel, setCognitiveLevel] = useState<'M1' | 'M2' | 'M3'>('M2')
  const [type, setType] = useState<QuestionType>('single-choice')
  const [stem, setStem] = useState('')
  const [passageTitle, setPassageTitle] = useState('')
  const [passageText, setPassageText] = useState('')
  const [choiceA, setChoiceA] = useState('')
  const [choiceB, setChoiceB] = useState('')
  const [choiceC, setChoiceC] = useState('')
  const [choiceD, setChoiceD] = useState('')
  const [correctChoice, setCorrectChoice] = useState('A')
  const [solution, setSolution] = useState('')
  const [hint1, setHint1] = useState('')
  const [hint2, setHint2] = useState('')
  const [estimatedSeconds, setEstimatedSeconds] = useState(90)

  useEffect(() => {
    localStorage.setItem('sangmath_custom_questions', JSON.stringify(questions))
  }, [questions])

  // HIGH-SECURITY EXECUTIVE ADMIN PORTAL LOGIN GATE
  if (!isAdmin) {
    return (
      <div style={{ minHeight: '85vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', padding: '2rem 1rem' }}>
        <div style={{ maxWidth: '460px', width: '100%', background: 'rgba(30, 41, 59, 0.85)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '28px', padding: '2.5rem 2rem', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)', textAlign: 'center', color: '#f8fafc' }}>
          <div style={{ width: 76, height: 76, borderRadius: '22px', background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.2rem', margin: '0 auto 1.25rem', boxShadow: '0 10px 20px rgba(59, 130, 246, 0.3)' }}>
            🛡️
          </div>
          
          <h2 style={{ fontSize: '1.75rem', fontWeight: 900, color: '#ffffff', letterSpacing: '-0.02em', marginBottom: '0.4rem' }}>
            Math ĐGNL Admin Portal
          </h2>
          <p style={{ color: '#94a3b8', fontSize: '0.92rem', lineHeight: 1.5, marginBottom: '1.75rem' }}>
            Cổng quản trị dành riêng cho Ban chuyên môn và Quản trị viên hệ thống.
          </p>

          {user ? (
            <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', padding: '0.85rem 1rem', borderRadius: '14px', marginBottom: '1.5rem', textAlign: 'left' }}>
              <small style={{ color: '#fca5a5', display: 'block', fontSize: '0.8rem' }}>Tài khoản đang mở:</small>
              <b style={{ color: '#ef4444', fontSize: '0.95rem' }}>{user.email}</b>
              <p style={{ color: '#f8fafc', fontSize: '0.82rem', margin: '0.2rem 0 0' }}>⚠️ Tài khoản này không có quyền truy cập Cổng Admin.</p>
            </div>
          ) : null}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            <button 
              onClick={openAuth}
              style={{ width: '100%', padding: '0.85rem 1.25rem', borderRadius: '14px', background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', color: '#ffffff', fontWeight: 800, fontSize: '1rem', border: 'none', cursor: 'pointer', boxShadow: '0 4px 14px rgba(37, 99, 235, 0.4)', transition: 'transform 0.15s ease' }}
            >
              🔑 Đăng Nhập Xác Thực Admin
            </button>
            <button 
              onClick={() => go('home')}
              style={{ width: '100%', padding: '0.75rem 1.25rem', borderRadius: '14px', background: 'transparent', color: '#94a3b8', fontWeight: 700, fontSize: '0.9rem', border: '1px solid rgba(255, 255, 255, 0.15)', cursor: 'pointer' }}
            >
              🏠 Quay Về Trang Chủ Học Sinh
            </button>
          </div>
        </div>
      </div>
    )
  }

  const handleSaveQuestion = (e: React.FormEvent) => {
    e.preventDefault()

    const newQuestion: Question = {
      id: editingId || `Q_${Date.now()}`,
      exam,
      domain,
      topic,
      knowledge: [knowledge],
      competency: [competencies],
      difficulty: cognitiveLevel === 'M1' ? 2 : cognitiveLevel === 'M2' ? 3 : 4,
      cognitiveLevel,
      type,
      passageTitle: passageTitle.trim() || undefined,
      passageText: passageText.trim() || undefined,
      stem,
      choices: [
        { id: 'A', text: choiceA || 'Đáp án A' },
        { id: 'B', text: choiceB || 'Đáp án B' },
        { id: 'C', text: choiceC || 'Đáp án C' },
        { id: 'D', text: choiceD || 'Đáp án D' }
      ],
      answer: correctChoice,
      solution,
      hint1: hint1 || 'Gợi ý nấc 1: Xác định từ khóa quan trọng trong đề bài.',
      hint2: hint2 || 'Gợi ý nấc 2: Lập sơ đồ biểu diễn các mối quan hệ kéo theo.',
      estimatedSeconds: Number(estimatedSeconds) || 90,
      tags: [exam, topic],
      variantGroup: editingId || `Q_${Date.now()}`,
      prerequisites: [knowledge],
      misconceptionOptions: knowledge === 'logic'
        ? ['condition-reversal', 'strategy-choice', 'careless-reading', 'time-pressure']
        : knowledge === 'data'
          ? ['data-reading', 'calculation-slip', 'careless-reading', 'time-pressure']
          : ['concept-gap', 'calculation-slip', 'strategy-choice', 'time-pressure'],
    }

    if (editingId) {
      setQuestions(prev => prev.map(q => q.id === editingId ? newQuestion : q))
    } else {
      setQuestions(prev => [newQuestion, ...prev])
    }

    resetForm()
    setActiveTab('list')
  }

  const resetForm = () => {
    setEditingId(null)
    setStem('')
    setPassageTitle('')
    setPassageText('')
    setChoiceA('')
    setChoiceB('')
    setChoiceC('')
    setChoiceD('')
    setSolution('')
    setHint1('')
    setHint2('')
  }

  const handleEdit = (q: Question) => {
    setEditingId(q.id)
    setExam(q.exam)
    setDomain(q.domain)
    setTopic(q.topic)
    setKnowledge(q.knowledge[0] || 'algebra')
    setCompetencies(q.competency[0] || 'problem-solving')
    setCognitiveLevel(q.cognitiveLevel)
    setType(q.type)
    setStem(q.stem)
    setPassageTitle(q.passageTitle || '')
    setPassageText(q.passageText || '')
    if (q.choices) {
      setChoiceA(q.choices[0]?.text || '')
      setChoiceB(q.choices[1]?.text || '')
      setChoiceC(q.choices[2]?.text || '')
      setChoiceD(q.choices[3]?.text || '')
    }
    setCorrectChoice(typeof q.answer === 'string' ? q.answer : 'A')
    setSolution(q.solution)
    setHint1(q.hint1)
    setHint2(q.hint2)
    setEstimatedSeconds(q.estimatedSeconds)
    setActiveTab('create')
  }

  const handleDelete = (id: string) => {
    if (confirm('Bạn có chắc chắn muốn xóa câu hỏi này khỏi kho đề?')) {
      setQuestions(prev => prev.filter(q => q.id !== id))
    }
  }

  const handleExportJSON = () => {
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(questions, null, 2))
    const downloadAnchor = document.createElement('a')
    downloadAnchor.setAttribute("href", dataStr)
    downloadAnchor.setAttribute("download", `kho_de_sangmath_dgnl_${Date.now()}.json`)
    document.body.appendChild(downloadAnchor)
    downloadAnchor.click()
    downloadAnchor.remove()
  }

  return (
    <div className="container py-6">
      {/* Header Banner */}
      <div className="card mb-6" style={{ background: 'linear-gradient(135deg, #1e293b, #0f172a)', color: 'white', borderRadius: '20px', padding: '1.5rem 2rem' }}>
        <div className="flex justify-between items-center">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span style={{ background: 'var(--orange-primary)', padding: '0.2rem 0.6rem', borderRadius: '100px', fontSize: '0.78rem', fontWeight: 800 }}>ADMIN CMS</span>
              <span style={{ fontSize: '0.88rem', color: '#94a3b8' }}>Dành riêng cho: <b>{user?.email || 'nguyensangnch@gmail.com'}</b></span>
            </div>
            <h2 style={{ fontSize: '1.8rem', margin: 0, fontWeight: 900 }}>🛠️ Studio Soạn Thảo & Quản Lý Kho Đề ĐGNL</h2>
          </div>
          <button className="btn btn-secondary" onClick={() => go('practice')} style={{ borderRadius: '100px' }}>
            📝 Xem giao diện Luyện tập
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex gap-3 mb-6" style={{ borderBottom: '2px solid var(--border-color)', paddingBottom: '0.5rem' }}>
        <button 
          className={`btn ${activeTab === 'list' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('list')}
          style={{ borderRadius: '12px' }}
        >
          📋 Kho câu hỏi hiện có ({questions.length})
        </button>
        <button 
          className={`btn ${activeTab === 'create' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => { resetForm(); setActiveTab('create'); }}
          style={{ borderRadius: '12px' }}
        >
          ➕ {editingId ? 'Sửa câu hỏi' : 'Soạn câu hỏi mới'}
        </button>
        <button 
          className={`btn ${activeTab === 'export' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('export')}
          style={{ borderRadius: '12px' }}
        >
          💾 Đồng bộ & Xuất lưu trữ (0đ Chi phí)
        </button>
      </div>

      {/* TAB 1: QUESTION LIST */}
      {activeTab === 'list' && (
        <div className="flex flex-col gap-4">
          {questions.map((q, idx) => (
            <div key={q.id} className="card" style={{ borderRadius: '16px', border: '1px solid var(--border-color)' }}>
              <div className="flex justify-between items-center mb-2 pb-2" style={{ borderBottom: '1px solid var(--border-color)' }}>
                <div className="flex items-center gap-2">
                  <span style={{ fontWeight: 800, color: 'var(--orange-primary)' }}>Câu {idx + 1}</span>
                  <span className="pill" style={{ fontSize: '0.75rem' }}>{q.exam}</span>
                  <span className="pill text-muted" style={{ fontSize: '0.75rem' }}>{q.topic}</span>
                  <span className="pill" style={{ background: '#e0f2fe', color: '#0369a1', fontSize: '0.75rem' }}>{q.cognitiveLevel}</span>
                </div>
                <div className="flex gap-2">
                  <button className="btn btn-secondary" onClick={() => handleEdit(q)} style={{ padding: '0.25rem 0.65rem', fontSize: '0.82rem' }}>✏️ Sửa</button>
                  <button className="btn" onClick={() => handleDelete(q.id)} style={{ background: '#fee2e2', color: '#991b1b', border: 'none', padding: '0.25rem 0.65rem', fontSize: '0.82rem' }}>🗑️ Xóa</button>
                </div>
              </div>

              {q.passageTitle && (
                <div className="mb-2 p-3" style={{ background: '#f8fafc', borderRadius: '8px', borderLeft: '3px solid var(--orange-primary)' }}>
                  <b>📖 Bài đọc: {q.passageTitle}</b>
                  <p style={{ fontSize: '0.88rem', margin: '0.2rem 0 0', color: '#475569' }}>{q.passageText}</p>
                </div>
              )}

              <p style={{ fontWeight: 700, fontSize: '1.02rem', margin: '0 0 0.5rem' }}>{q.stem}</p>

              {q.choices && (
                <div className="grid grid-cols-2 gap-2 mb-3">
                  {q.choices.map(c => (
                    <div 
                      key={c.id} 
                      style={{ 
                        padding: '0.5rem 0.75rem', 
                        borderRadius: '8px', 
                        border: '1px solid var(--border-color)',
                        background: c.id === q.answer ? '#dcfce7' : 'white',
                        borderColor: c.id === q.answer ? '#22c55e' : 'var(--border-color)',
                        fontSize: '0.9rem'
                      }}
                    >
                      <b>{c.id}.</b> {c.text}
                    </div>
                  ))}
                </div>
              )}

              <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                💡 <b>Nấc 1:</b> {q.hint1} | 💡 <b>Nấc 2:</b> {q.hint2}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* TAB 2: CREATE / EDIT QUESTION */}
      {activeTab === 'create' && (
        <form onSubmit={handleSaveQuestion} className="card max-w-3xl mx-auto" style={{ borderRadius: '20px', padding: '1.75rem' }}>
          <h3 style={{ margin: '0 0 1.25rem', fontSize: '1.4rem' }}>
            {editingId ? '✏️ Chỉnh Sửa Câu Hỏi' : '➕ Soạn Câu Hỏi Mới Kho Math ĐGNL'}
          </h3>

          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <label style={{ fontWeight: 700, fontSize: '0.88rem' }}>Kỳ thi:</label>
              <select className="btn btn-secondary" value={exam} onChange={e => setExam(e.target.value as Exam)} style={{ width: '100%', marginTop: '0.2rem' }}>
                <option value="HSA">HSA (ĐHQG Hà Nội)</option>
                <option value="TSA">TSA (ĐH Bách Khoa HN)</option>
                <option value="VACT">V-ACT (ĐHQG TP.HCM)</option>
              </select>
            </div>
            <div>
              <label style={{ fontWeight: 700, fontSize: '0.88rem' }}>Cấp độ nhận thức:</label>
              <select className="btn btn-secondary" value={cognitiveLevel} onChange={e => setCognitiveLevel(e.target.value as any)} style={{ width: '100%', marginTop: '0.2rem' }}>
                <option value="M1">M1 - Nhận biết / Thông hiểu</option>
                <option value="M2">M2 - Vận dụng</option>
                <option value="M3">M3 - Vận dụng cao / Suy luận</option>
              </select>
            </div>
            <div>
              <label style={{ fontWeight: 700, fontSize: '0.88rem' }}>Dạng câu hỏi:</label>
              <select className="btn btn-secondary" value={type} onChange={e => setType(e.target.value as any)} style={{ width: '100%', marginTop: '0.2rem' }}>
                <option value="single-choice">Trắc nghiệm 4 lựa chọn</option>
                <option value="numeric">Điền đáp số ngắn</option>
                <option value="true-false">Đúng / Sai</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label style={{ fontWeight: 700, fontSize: '0.88rem' }}>Chủ đề bài tập:</label>
              <input type="text" className="card" value={topic} onChange={e => setTopic(e.target.value)} placeholder="Ví dụ: Đại số & Suy luận" style={{ width: '100%', marginTop: '0.2rem', padding: '0.6rem' }} required />
            </div>
            <div>
              <label style={{ fontWeight: 700, fontSize: '0.88rem' }}>Mảng kiến thức (taxonomy):</label>
              <select className="btn btn-secondary" value={knowledge} onChange={e => setKnowledge(e.target.value as KnowledgeCode)} style={{ width: '100%', marginTop: '0.2rem' }}>
                {Object.entries(KNOWLEDGE).map(([code, label]) => <option key={code} value={code}>{label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontWeight: 700, fontSize: '0.88rem' }}>Năng lực kiểm tra (taxonomy):</label>
              <select className="btn btn-secondary" value={competencies} onChange={e => setCompetencies(e.target.value as CompetencyCode)} style={{ width: '100%', marginTop: '0.2rem' }}>
                {Object.entries(COMPETENCIES).map(([code, label]) => <option key={code} value={code}>{label}</option>)}
              </select>
            </div>
          </div>

          <div className="mb-4">
            <label style={{ fontWeight: 700, fontSize: '0.88rem' }}>Tiêu đề bài đọc chùm (Nếu có):</label>
            <input type="text" className="card" value={passageTitle} onChange={e => setPassageTitle(e.target.value)} placeholder="Nhập tiêu đề giả thiết chung/bài đọc..." style={{ width: '100%', marginTop: '0.2rem', padding: '0.6rem' }} />
          </div>

          <div className="mb-4">
            <label style={{ fontWeight: 700, fontSize: '0.88rem' }}>Nội dung bài đọc / Giả thiết chung (Nếu có):</label>
            <textarea className="card" value={passageText} onChange={e => setPassageText(e.target.value)} rows={3} placeholder="Nhập văn bản bài đọc dùng chung..." style={{ width: '100%', marginTop: '0.2rem', padding: '0.6rem' }} />
          </div>

          <div className="mb-4">
            <label style={{ fontWeight: 700, fontSize: '0.88rem' }}>Câu hỏi chính (Stem):</label>
            <textarea className="card" value={stem} onChange={e => setStem(e.target.value)} rows={3} placeholder="Nhập nội dung câu hỏi..." style={{ width: '100%', marginTop: '0.2rem', padding: '0.6rem' }} required />
          </div>

          {type === 'single-choice' && (
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label style={{ fontWeight: 700, fontSize: '0.85rem' }}>Lựa chọn A:</label>
                <input type="text" className="card" value={choiceA} onChange={e => setChoiceA(e.target.value)} style={{ width: '100%', padding: '0.5rem' }} required />
              </div>
              <div>
                <label style={{ fontWeight: 700, fontSize: '0.85rem' }}>Lựa chọn B:</label>
                <input type="text" className="card" value={choiceB} onChange={e => setChoiceB(e.target.value)} style={{ width: '100%', padding: '0.5rem' }} required />
              </div>
              <div>
                <label style={{ fontWeight: 700, fontSize: '0.85rem' }}>Lựa chọn C:</label>
                <input type="text" className="card" value={choiceC} onChange={e => setChoiceC(e.target.value)} style={{ width: '100%', padding: '0.5rem' }} required />
              </div>
              <div>
                <label style={{ fontWeight: 700, fontSize: '0.85rem' }}>Lựa chọn D:</label>
                <input type="text" className="card" value={choiceD} onChange={e => setChoiceD(e.target.value)} style={{ width: '100%', padding: '0.5rem' }} required />
              </div>
            </div>
          )}

          <div className="mb-4">
            <label style={{ fontWeight: 700, fontSize: '0.88rem' }}>Đáp án đúng:</label>
            <select className="btn btn-secondary" value={correctChoice} onChange={e => setCorrectChoice(e.target.value)} style={{ marginLeft: '0.5rem' }}>
              <option value="A">Đáp án A</option>
              <option value="B">Đáp án B</option>
              <option value="C">Đáp án C</option>
              <option value="D">Đáp án D</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label style={{ fontWeight: 700, fontSize: '0.85rem' }}>Gợi ý nấc 1 (Định hướng chìa khóa):</label>
              <textarea className="card" value={hint1} onChange={e => setHint1(e.target.value)} rows={2} style={{ width: '100%', marginTop: '0.2rem', padding: '0.5rem' }} />
            </div>
            <div>
              <label style={{ fontWeight: 700, fontSize: '0.85rem' }}>Gợi ý nấc 2 (Sơ đồ suy luận trung gian):</label>
              <textarea className="card" value={hint2} onChange={e => setHint2(e.target.value)} rows={2} style={{ width: '100%', marginTop: '0.2rem', padding: '0.5rem' }} />
            </div>
          </div>

          <div className="mb-6">
            <label style={{ fontWeight: 700, fontSize: '0.88rem' }}>Lời giải chi tiết:</label>
            <textarea className="card" value={solution} onChange={e => setSolution(e.target.value)} rows={3} placeholder="Nhập các bước chứng minh và chọn đáp án..." style={{ width: '100%', marginTop: '0.2rem', padding: '0.6rem' }} required />
          </div>

          <div className="flex justify-end gap-3">
            <button type="button" className="btn btn-secondary" onClick={() => setActiveTab('list')}>Hủy</button>
            <button type="submit" className="btn btn-primary" style={{ padding: '0.75rem 2rem' }}>
              💾 {editingId ? 'Cập Nhật Câu Hỏi' : 'Lưu Câu Hỏi Vào Kho'}
            </button>
          </div>
        </form>
      )}

      {/* TAB 3: EXPORT & ZERO COST EXPLANATION */}
      {activeTab === 'export' && (
        <div className="card max-w-3xl mx-auto py-8" style={{ borderRadius: '20px' }}>
          <h3 style={{ fontSize: '1.5rem', marginBottom: '0.75rem' }}>💾 Chiến Lược Lưu Trữ Kho Đề 0 VNĐ Chi Phí</h3>
          
          <div className="card mb-6" style={{ background: '#f8fafc', padding: '1rem 1.25rem', borderRadius: '12px', borderLeft: '4px solid var(--green-primary)' }}>
            <h4 style={{ margin: '0 0 0.5rem', color: 'var(--green-primary)' }}>✅ Hiện tại hệ thống đang lưu ở đâu?</h4>
            <p style={{ fontSize: '0.92rem', margin: 0, lineHeight: 1.6 }}>
              1. **Bản cài sẵn (Built-in)**: Lưu trực tiếp trong gói mã nguồn tĩnh `apps/web/src/data/logic_questions.ts` trên Firebase Hosting CDN (không tốn tiền server).<br />
              2. **Bản soạn mới (Local Store)**: Lưu trực tiếp trong bộ nhớ trình duyệt `localStorage` của Admin **nguyensangnch@gmail.com** (hoàn toàn miễn phí).
            </p>
          </div>

          <div className="flex gap-4 mb-6">
            <button className="btn btn-primary" onClick={handleExportJSON} style={{ padding: '0.8rem 1.8rem', borderRadius: '100px' }}>
              📥 Xuất File JSON Kho Đề Dự Phòng (.json)
            </button>
          </div>

          <div className="card" style={{ background: '#fffbe8', borderColor: '#f59e0b' }}>
            <b>☁️ Khi muốn đồng bộ Firestore Cloud:</b>
            <p style={{ fontSize: '0.88rem', margin: '0.4rem 0 0', lineHeight: 1.5 }}>
              Firebase Firestore cho phép bạn đọc <b>50.000 lượt/ngày</b> và ghi <b>20.000 lượt/ngày</b> hoàn toàn <b>MIỄN PHÍ 100%</b> trên dự án Google <b>`stexgv`</b>.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
